import { mapActions, mapGetters, mapState } from 'vuex';

import {
  getMenu,
  getSharedMenu,
} from '@/api/menu';
import ContinueShareLink from '@/components/ContinueShareLink/ContinueShareLink.vue';
import MenuItemDetails from '@/components/MenuItemDetails/MenuItemDetails.vue';
import MenuItems from '@/components/MenuItems/MenuItems.vue';
import MenuTopInfos from '@/components/MenuTopInfos/MenuTopInfos.vue';
import bus from '@/utils/bus';
import {
  CONSUMPTION_LIMIT,
  MENU_LIST_ITEM_STATUSES,
  SNACKBAR_TYPES,
} from '@/utils/constants';
import { getMenuTopToContentHeight } from '@/utils/helper';
import { ERROR_RECORD_NOT_FOUND } from '@/utils/errorCodeList';

const ON_SITE_ORDER_ITEM_INDEX = -1;

export default {
  name: 'Menu',
  components: {
    ContinueShareLink,
    MenuItemDetails,
    MenuItems,
    MenuTopInfos,
  },
  data() {
    return {
      isValid: false,
      showContinueShareLinkModal: false,
      showDetails: false,
    };
  },
  computed: {
    ...mapGetters('cart', [
      'cartItems',
      'cartItemsCount',
      'cartTotal',
    ]),
    ...mapGetters('user', [
      'isAuthenticated',
    ]),
    ...mapState('menu', [
      'activatedCategory',
      'gettingMenu',
      'memberOrders',
      'menu',
    ]),
    aboutToStart() {
      return `此團購即將在
        ${this.expectedStartPeriod?.[0]?.start_date}
        ${this.expectedStartPeriod?.[0]?.start_weekday}
        ${this.expectedStartPeriod?.[0]?.start_time}
      開放訂購，敬請期待！`;
    },
    categories() {
      return this.menu?.items?.categories
        ?.filter(({ index }) => index !== ON_SITE_ORDER_ITEM_INDEX) || [];
    },
    consumptionLimitText() {
      const amount = this.$options.filters.commaFormat(this.infos?.limit_amount);
      const limits = {
        [CONSUMPTION_LIMIT.AMOUNT]: `每人限制總消費金額 ${this.$options.filters.priceFormat(amount)}`,
        [CONSUMPTION_LIMIT.ITEMS]: `每人限制總訂購項目數 ${amount} 項`,
      };
      return limits[this.infos?.limit];
    },
    expectedStartPeriod() {
      const expiredPeriods = this.infos?.expired_periods;
      if (!expiredPeriods) {
        return [];
      }

      const arr = [...expiredPeriods];
      arr.shift();

      return arr;
    },
    hasCartItems() {
      return !!this.cartItems?.length;
    },
    hasConsumptionLimit() {
      return !!this.infos?.limit;
    },
    hasShareKey() {
      return !!this.shareKey;
    },
    infos() {
      return this.menu?.infos || {};
    },
    isScheduled() {
      return this.infos?.status === MENU_LIST_ITEM_STATUSES.SCHEDULED;
    },
    items() {
      return this.categories || [];
    },
    lastOrderPeriod() {
      const expiredPeriods = this.infos?.expired_periods;
      if (!expiredPeriods) {
        return [];
      }

      const arr = [...expiredPeriods];
      arr.pop();

      return arr;
    },
    newsList() {
      const news = [this.consumptionLimitText];
      if (this.isScheduled) {
        news.unshift(this.aboutToStart);
      }
      return news;
    },
    orders() {
      return this.memberOrders || [];
    },
    periods() {
      return this.isScheduled ? [{
        title: '預計開放訂購時段',
        periods: this.expectedStartPeriod || [],
      }] : [{
        title: '最後訂購時間',
        periods: this.lastOrderPeriod || [],
      }];
    },
    shareKey() {
      const { share } = this.$route?.query || {};
      return share;
    },
    showNews() {
      return !!(this.isScheduled || this.hasConsumptionLimit) && !this.hasShareKey;
    },
  },
  async created() {
    if (!this.isAuthenticated && !this.shareKey) {
      this.$router.replace('/login');
      return;
    }

    if (this.isAuthenticated && this.shareKey) {
      this.showContinueShareLinkModal = true;
      return;
    }

    this.isValid = true;
    this.loadMenu();

    bus.$on('REFRESH_MENU', async () => {
      this.loadMenu();
    });
  },
  beforeDestroy() {
    Object.assign(this.$data, this.$options.data.apply(this));
  },
  watch: {
    activatedCategory(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        const arr = newVal.split('-');
        const index = arr[arr.length - 1];
        const element = document.getElementById(`scroll-tab-${index}`);
        if (element) {
          this.scrollIntoView(element);
        }
      }
    },
  },
  methods: {
    ...mapActions('cart', {
      clearCart: 'CLEAR_CART_ITEMS',
    }),
    ...mapActions('menu', {
      getMenu: 'GET_MENU',
      setActivatedCategory: 'SET_ACTIVATED_CATEGORY',
    }),
    async loadMenu() {
      if (this.shareKey) {
        await this.getCustomSharedMenu();
      } else {
        await this.getCurrentMenu();
      }
    },
    async getCurrentMenu() {
      try {
        const id = this.$route?.params?.id;

        if (!id) {
          const error = new Error();
          error.status = ERROR_RECORD_NOT_FOUND;
          throw error;
        }
        await this.getMenu({
          cb: getMenu,
          key: id,
        });
        const { items: { categories = [] } = {} } = this.menu || {};
        const filteredCategories = categories
          .filter(({ index }) => index !== ON_SITE_ORDER_ITEM_INDEX);
        const firstCategoryId = filteredCategories?.[0]?.index;
        if (Number.isInteger(firstCategoryId)) {
          this.setActivatedCategory(`menu-category-${firstCategoryId}`);
        }
      } catch (error) {
        const { message: msg = '找不到團購', status } = error;
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          msg,
        });
        if (status === ERROR_RECORD_NOT_FOUND) {
          this.$router.replace('/menu');
        }
      }
    },
    async getCustomSharedMenu() {
      try {
        await this.getMenu({
          cb: getSharedMenu,
          key: this.shareKey,
        });
      } catch (error) {
        const { message: msg = '' } = error;
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          msg,
        });
        await this.clearCart();
        this.$router.replace('/login');
      }
    },
    scrollTo(categoryId) {
      const categoryElement = document.getElementById(`menu-category-${categoryId}`);
      const top = categoryElement.offsetTop - getMenuTopToContentHeight();
      window.scrollTo({
        top,
        left: 0,
        behavior: 'smooth',
      });
      this.setActivatedCategory(`menu-category-${categoryId}`);
    },
    scrollIntoView(el) {
      const { offsetLeft, clientWidth } = el;
      const tabs = document.getElementById('menu-scrollbar');
      const offsetCentered = offsetLeft + clientWidth / 2 - tabs.clientWidth / 2;
      const offset = Math.max(0, offsetCentered);
      tabs.scrollLeft = offset;
    },
    showCart() {
      bus.$emit('SHOW_CART');
    },
    showMenuItemDetails(itemIndex) {
      this.showDetails = true;
      const {
        enable_order_note: enableOrderNote = false,
        entree_limit: entreeLimit = {},
      } = this.menu || {};
      bus.$emit('SHOW_MENU_ITEM_DETAILS', {
        entree: this.menu?.items?.getEntree(itemIndex) || {},
        menuId: this.$route?.params?.id,
        name: this.infos?.name,
        entreeLimit: entreeLimit[itemIndex] || {},
        enableOrderNote,
      });
    },
  },
};
